// ============================================================================
// VariantenPanel — Angebotsvarianten Basis / Komfort / Premium
// ----------------------------------------------------------------------------
// Ruft POST /projects/:id/konzept/variants (reasoning-core buildVariants über die
// API-View). Zeigt 3 Pakete als Karten + Empfehlung + Preis-Annahmen. Preise
// sind Richtwerte und im Panel überschreibbar (Transparenz, kein Festpreis).
// Interne Sicht (Investition sichtbar) — kein Kunden-PDF, keine Freigabe.
// ============================================================================
import { useState } from "react";
import type { CSSProperties } from "react";
import { apiClient } from "@/lib/api-client.js";
import { eur, num } from "@/lib/format.js";

interface VariantPlan {
  id: "basis" | "komfort" | "premium";
  label: string;
  beschreibung: string;
  komponenten: string[];
  hasBattery: boolean;
  batteryKwh: number;
  hasNotstrom: boolean;
  hasEnergiemanagement: boolean;
  investitionEur: number;
  foerderungEur: number;
  eigenverbrauchKwh: number;
  einspeisungKwh: number;
  netzbezugKwh: number;
  autarkieProzent: number;
  eigenverbrauchsquoteProzent: number;
  ersparnisJahr1Eur: number;
  amortisationJahre: number | null;
  amortisationMitFoerderungJahre: number | null;
}

interface VariantsResponse {
  detail: { variants: VariantPlan[]; empfehlungId: string; begruendung: string };
  annahmen: {
    pvPreisAusKalkulation: boolean;
    pvPricePerKwpEur: number;
    batteryPricePerKwhEur: number;
    notstromEur: number;
    emsEur: number;
    bundesland: string | null;
  };
  resolved: { kwp: number; annualConsumptionKwh: number; specificYieldKwhPerKwp: number; yieldSource: string; yieldTierLabel?: string; basePvInvestEur: number | null };
}

interface Overrides {
  pvPricePerKwpEur?: number | undefined;
  batteryPricePerKwhEur?: number | undefined;
  notstromEur?: number | undefined;
  emsEur?: number | undefined;
  komfortBatteryKwh?: number | undefined;
}

const BRAND_ORANGE = "#E8462A";

export function VariantenPanel({ projectId }: { projectId: string }) {
  const [data, setData] = useState<VariantsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrices, setShowPrices] = useState(false);
  const [ov, setOv] = useState<Overrides>({});

  const compute = async () => {
    setLoading(true);
    setError(null);
    const body: Record<string, number> = {};
    for (const [k, v] of Object.entries(ov)) if (typeof v === "number" && Number.isFinite(v) && v > 0) body[k] = v;
    const res = await apiClient.post<VariantsResponse>(`/projects/${projectId}/konzept/variants`, body);
    if (res.success) setData(res.data);
    else setError(res.error?.message ?? "Varianten konnten nicht berechnet werden.");
    setLoading(false);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Angebotsvarianten</h3>
          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Basis · Komfort · Premium — automatisch aus den Projektdaten</span>
        </div>
        <button type="button" onClick={compute} disabled={loading} className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }}>
          {loading ? "Berechne…" : data ? "Neu berechnen" : "Varianten berechnen"}
        </button>
      </div>

      <button type="button" onClick={() => setShowPrices((s) => !s)} style={{ fontSize: 12, color: "var(--color-text-muted)", background: "none", border: "none", cursor: "pointer", padding: "2px 0", marginBottom: showPrices ? 8 : 0 }}>
        {showPrices ? "▾" : "▸"} Preis-Richtwerte anpassen
      </button>
      {showPrices && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 10 }}>
          <PriceInput label="PV €/kWp" value={ov.pvPricePerKwpEur} onChange={(v) => setOv({ ...ov, pvPricePerKwpEur: v })} placeholder="1400" />
          <PriceInput label="Speicher €/kWh" value={ov.batteryPricePerKwhEur} onChange={(v) => setOv({ ...ov, batteryPricePerKwhEur: v })} placeholder="600" />
          <PriceInput label="Speicher kWh (Komfort)" value={ov.komfortBatteryKwh} onChange={(v) => setOv({ ...ov, komfortBatteryKwh: v })} placeholder="auto" />
          <PriceInput label="Notstrom €" value={ov.notstromEur} onChange={(v) => setOv({ ...ov, notstromEur: v })} placeholder="1500" />
          <PriceInput label="EMS €" value={ov.emsEur} onChange={(v) => setOv({ ...ov, emsEur: v })} placeholder="800" />
        </div>
      )}

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 10, fontSize: 13, color: "#dc2626", marginTop: 8 }}>{error}</div>
      )}

      {data && (
        <>
          {/* Empfehlung */}
          <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: 10, fontSize: 13, margin: "10px 0" }}>
            <strong style={{ color: "#15803d" }}>Empfehlung:</strong> {data.detail.begruendung}
          </div>

          {/* 3 Karten */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            {data.detail.variants.map((v) => {
              const recommended = v.id === data.detail.empfehlungId;
              return (
                <div
                  key={v.id}
                  style={{
                    border: recommended ? `2px solid ${BRAND_ORANGE}` : "1px solid var(--color-border)",
                    borderRadius: 8,
                    padding: 14,
                    position: "relative",
                    background: "var(--color-bg, #fff)",
                  }}
                >
                  {recommended && (
                    <span style={{ position: "absolute", top: -10, right: 12, background: BRAND_ORANGE, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>EMPFOHLEN</span>
                  )}
                  <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{v.label}</h4>
                  <p style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, minHeight: 48 }}>{v.beschreibung}</p>

                  <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>{eur(v.investitionEur)}</div>
                  {v.foerderungEur > 0 && (
                    <div style={{ fontSize: 11, color: "#15803d", marginBottom: 8 }}>− {eur(v.foerderungEur)} Förderung möglich</div>
                  )}

                  <Row label="Autarkie" value={`${v.autarkieProzent} %`} strong />
                  <Row label="Eigenverbrauch" value={`${num(v.eigenverbrauchKwh, 0)} kWh`} />
                  <Row label="Ersparnis Jahr 1" value={eur(v.ersparnisJahr1Eur)} />
                  <Row label="Amortisation" value={v.amortisationJahre != null ? `${num(v.amortisationJahre, 1)} J` : "—"} />
                  {v.amortisationMitFoerderungJahre != null && (
                    <Row label="…mit Förderung" value={`${num(v.amortisationMitFoerderungJahre, 1)} J`} />
                  )}

                  <div style={{ marginTop: 10, borderTop: "1px solid var(--color-border)", paddingTop: 8 }}>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 11, color: "var(--color-text-muted)" }}>
                      {v.komponenten.map((k, i) => (
                        <li key={i} style={{ padding: "1px 0" }}>• {k}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Annahmen / Transparenz */}
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
            Grundlage: {num(data.resolved.kwp, 2)} kWp · {num(data.resolved.annualConsumptionKwh, 0)} kWh/Jahr ·
            {" "}{num(data.resolved.specificYieldKwhPerKwp, 0)} kWh/kWp Ertrag ({data.resolved.yieldTierLabel ?? yieldLabel(data.resolved.yieldSource)}).
            {" "}PV-Preis {data.annahmen.pvPreisAusKalkulation ? "aus echter Kalkulation" : `Richtwert ${eur(data.annahmen.pvPricePerKwpEur)}/kWp`},
            {" "}Speicher Richtwert {eur(data.annahmen.batteryPricePerKwhEur)}/kWh.
            {data.annahmen.bundesland ? ` Förderung für ${data.annahmen.bundesland} (Bund + Land).` : " Förderung: nur Bund (Bundesland offen)."}
            {" "}Alle Preise sind unverbindliche Richtwerte — interne Sicht, kein Kundenangebot.
          </div>
        </>
      )}
    </div>
  );
}

function yieldLabel(source: string): string {
  if (source === "simulation") return "aus Simulation";
  if (source === "provided") return "vorgegeben";
  return "Richtwert 950";
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 500 }}>{value}</span>
    </div>
  );
}

function PriceInput({ label, value, onChange, placeholder }: { label: string; value: number | undefined; onChange: (v: number | undefined) => void; placeholder: string }) {
  const wrap: CSSProperties = { display: "flex", flexDirection: "column", gap: 2 };
  return (
    <label style={wrap}>
      <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{label}</span>
      <input
        type="number"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        style={{ padding: "4px 6px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 4 }}
      />
    </label>
  );
}
